import type { DTOOf, IRuleValidationRequest, IRuleValidationResult } from '@atlas/types'
import { RuleValidationTargetType } from '@atlas/types'
import { apiClient } from './api.client'

/** One validation run, as the list endpoint returns it. */
export type RuleValidationRun = DTOOf<IRuleValidationRequest>

/** One immutable per-rule audit record. */
export type RuleValidationRecord = DTOOf<IRuleValidationResult>

/** A run with its records — what the detail endpoint returns and the results table reads. */
export type RuleValidationRunDetail = RuleValidationRun & { results: RuleValidationRecord[] }

/** The three write entry points live on the services owning each target, not on rule-service. */
const REQUEST_PATH: Record<RuleValidationTargetType, string> = {
	[RuleValidationTargetType.FILE]: '/files',
	[RuleValidationTargetType.CAMPAIGN]: '/campaigns',
	[RuleValidationTargetType.WORK_ORDER]: '/work-orders',
}

export const ruleValidationApi = {
	/**
	 * Request a run against a file, a campaign or a work order. Advisory: nothing gates on
	 * the verdicts.
	 *
	 * `ruleIds` narrows the run to those rules, for a reader re-checking one rule rather
	 * than the whole target. Omitted covers every rule that resolves.
	 */
	request: (targetType: RuleValidationTargetType, targetId: string, ruleIds?: string[]): Promise<RuleValidationRun> =>
		apiClient
			.post<RuleValidationRun>(`${REQUEST_PATH[targetType]}/${targetId}/rule-validations`, ruleIds ? { ruleIds } : {})
			.then((r) => r.data),

	/**
	 * A target's runs, newest first. Always an array — a target with no run is an empty state, not an absence.
	 *
	 * A FILE goes to the per-file route, not to `/rules/validations`: that one gates on role
	 * alone, so reading a file's runs through it would hand a caller who cannot view the file
	 * the rationale and evidence quoted out of it.
	 */
	listForTarget: (targetType: RuleValidationTargetType, targetId: string): Promise<RuleValidationRun[]> =>
		(targetType === RuleValidationTargetType.FILE
			? apiClient.get<RuleValidationRun[]>(`/files/${targetId}/rule-validations`)
			: apiClient.get<RuleValidationRun[]>('/rules/validations', { params: { targetType, targetId } })
		).then((r) => r.data ?? []),

	/**
	 * One run with its per-rule audit records.
	 *
	 * Same ACL split as `listForTarget`: a FILE run's records quote the file's own content
	 * back in `rationale`/`evidence`, so they are read through the access-checked per-file
	 * route, not `/rules/validations/{requestId}`, which gates on role alone.
	 */
	get: (targetType: RuleValidationTargetType, targetId: string, requestId: string): Promise<RuleValidationRunDetail> =>
		(targetType === RuleValidationTargetType.FILE
			? apiClient.get<RuleValidationRunDetail>(`/files/${targetId}/rule-validations/${requestId}`)
			: apiClient.get<RuleValidationRunDetail>(`/rules/validations/${requestId}`)
		).then((r) => r.data),
}
